import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Receipt, Banknote, FileText, AlertCircle, CreditCard,
} from "lucide-react";
import { getDashboardSummary, getDashboardFinanceActivity, getDashboardFinanceTrend, getDashboardFinanceApSummary } from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, tenantHour } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";
import ModuleSetupCard from "../components/ModuleSetupCard";

const NAVY  = "#1e3a8a";
const TEAL  = "#0f766e";
const AMBER = "#f59e0b";
const RED   = "#dc2626";
const PURPLE = "#7c3aed";

const WHT_CATEGORY_LABELS: Record<string, string> = {
  professional_services: "Professional Services",
  advertising: "Advertising",
  rent: "Rent",
  transport: "Transport",
  other: "Other",
};
const CATEGORY_COLORS = [NAVY, TEAL, AMBER, PURPLE, RED];

// The feed is invoice-only — the leave branch that used to live here was dead
// weight from when this page shared Core's cross-module /activity endpoint.
function activityDot(type: string, status: string) {
  if (type === "invoice") return status === "paid" ? "#16a34a" : status === "overdue" ? RED : NAVY;
  return PURPLE;
}

function activityIcon(type: string) {
  if (type === "invoice") return <Receipt size={14} />;
  return <FileText size={14} />;
}

// Finance module's dashboard — module home for the Finance section (per-module-dashboards
// spec, Package 2). Extracted from the old isFinance-gated branch of the shared Core
// DashboardPage: same data, same endpoints, now its own route/page instead of living
// inside a generic dashboard that every other role also renders.
export default function FinanceDashboardPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();

  const { data: summary } = useQuery({ queryKey: ["dashboard-summary"], queryFn: getDashboardSummary, refetchInterval: 60_000 });
  // Invoice activity now has its own module-gated endpoint. It used to ride on
  // the Core /activity feed, which handed invoice rows to tenants without
  // Finance; that feed is Core-only (leave / expense / helpdesk) now.
  const { data: activity = [] } = useQuery({ queryKey: ["dashboard-finance-activity"], queryFn: getDashboardFinanceActivity, refetchInterval: 60_000 });
  const { data: financeTrend = [] } = useQuery({ queryKey: ["dashboard-finance-trend"], queryFn: getDashboardFinanceTrend, staleTime: 5 * 60_000 });
  // No period selector exists on this dashboard today — defaults to the current month,
  // same as the rest of the finance stats (revenue_this_month, draft_slips, etc.) above.
  const { data: apSummary } = useQuery({ queryKey: ["dashboard-finance-ap-summary"], queryFn: () => getDashboardFinanceApSummary(), staleTime: 5 * 60_000 });

  const firstName = (user?.name || "").split(" ")[0];
  const hour = tenantHour(tz);
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dayStr = formatDate(new Date(), "long", tz);

  return (
    <div>
      {/* No width cap: the module's own list pages (Candidates, Jobs, Offers,
          Interview Hub) all run the full content width, so capping the module
          landing page made the layout jump as you moved between them. */}

      {/* ── Greeting ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
          {greeting}, {firstName} 👋
        </h1>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>{dayStr} · Here's what needs your attention</p>
      </div>

      <ModuleSetupCard module="finance" />

      {/* ── Stat cards ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }} className="stat-cards-row">
        <StatCard label="Overdue Invoices" value={summary?.overdue_invoices ?? 0} color={RED}
          icon={<AlertCircle size={18} />} to="/invoices"
          subtitle={summary?.overdue_invoices > 0 ? "Past due date" : "None overdue"} />
        <StatCard label="Total Invoices" value={summary?.total_invoices ?? 0} color={TEAL}
          icon={<Receipt size={18} />} to="/invoices" subtitle="All time" />
        <StatCard label="Revenue This Month" value={summary?.revenue_this_month ?? 0} color={NAVY}
          icon={<Banknote size={18} />} to="/invoices" subtitle="Paid invoices" />
        <StatCard label="Pending Quotations" value={summary?.pending_quotations ?? 0} color={AMBER}
          icon={<FileText size={18} />} to="/quotations" subtitle="Draft or sent" />
        <StatCard label="Pending Expenses" value={summary?.pending_expenses ?? 0} color={PURPLE}
          icon={<CreditCard size={18} />} to="/expenses" subtitle="Awaiting approval" />
        <StatCard label="Draft Payslips" value={summary?.draft_slips ?? 0} color={TEAL}
          icon={<Banknote size={18} />} to="/payroll" subtitle="Not yet issued" />
      </div>

      {/* ── Quick actions ── */}
      <div style={{ background: "white", borderRadius: 10, padding: "20px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="New Invoice" icon={<Receipt size={14} />} to="/invoices" color={RED} />
          <QuickAction label="Quotations" icon={<FileText size={14} />} to="/quotations" color={TEAL} />
          <QuickAction label="Expenses" icon={<CreditCard size={14} />} to="/expenses" color={PURPLE} />
          <QuickAction label="Payroll" icon={<Banknote size={14} />} to="/payroll" color={NAVY} />
        </div>
      </div>

      {/* ── Two-column: chart + activity ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="grid-2col">

        <div style={{ background: "white", borderRadius: 10, padding: "22px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Invoice & Quotation Trend</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Paid invoices + quotations · last 6 months</div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={financeTrend as any[]} barSize={18} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
              <Tooltip cursor={{ fill: "#f8fafc" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="invoices" name="Paid Invoices" fill={TEAL} radius={[4, 4, 0, 0]} />
              <Bar dataKey="quotations" name="Quotations" fill={AMBER} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent activity */}
        <div style={{ background: "white", borderRadius: 10, padding: "22px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Recent Activity</div>
          {(activity as any[]).length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#64748b", fontSize: 13 }}>No recent activity</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {(activity as any[]).slice(0, 7).map((item: any, i: number) => (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "9px 0", borderBottom: i < 6 ? "1px solid #f1f5f9" : "none",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 1,
                    background: `${activityDot(item.type, item.status)}15`,
                    color: activityDot(item.type, item.status),
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {activityIcon(item.type)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.text}
                    </div>
                    {item.sub && <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>{item.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Accounts Payable ── */}
      {apSummary && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", marginBottom: 14 }}>Accounts Payable</div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 20 }} className="stat-cards-row">
            <StatCard label="Outstanding Payables" value={(apSummary as any).total_payables_outstanding ?? 0}
              color={NAVY} icon={<Banknote size={18} />} to="/vendor-bills" subtitle="Submitted + approved, unpaid" />
            <StatCard label="Overdue Bills" value={(apSummary as any).overdue_count ?? 0}
              color={(apSummary as any).overdue_count > 0 ? RED : TEAL} icon={<AlertCircle size={18} />} to="/vendor-bills"
              subtitle={(apSummary as any).overdue_count > 0 ? `${(apSummary as any).overdue_amount?.toLocaleString("en", { minimumFractionDigits: 2 })} past due` : "None overdue"} />
            <StatCard label="Input VAT Claimable" value={(apSummary as any).input_vat_claimable ?? 0}
              color={TEAL} icon={<FileText size={18} />} to="/vendor-bills" subtitle="This month" />
          </div>

          {/* AP aging table */}
          <div style={{ background: "white", borderRadius: 10, padding: "20px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>AP Aging</div>
            {/* overflow-x scoped here, not relying on .table-wrap's own breakpoint —
                min-width on the grid CONTAINER (rather than minmax() per track) hits a
                real Chromium quirk where every 1fr track collapses to the container's
                full width once available space drops below that min-width, stacking
                the 4 buckets into one column instead of side by side. */}
            <div className="table-wrap" style={{ overflowX: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(110px, 1fr))", gap: 12 }}>
                {[
                  { key: "current", label: "Current", overdue: false },
                  { key: "overdue_1_30", label: "1–30 days", overdue: true },
                  { key: "overdue_31_60", label: "31–60 days", overdue: true },
                  { key: "overdue_60_plus", label: "60+ days", overdue: true },
                ].map(({ key, label, overdue }) => {
                  const bucket = (apSummary as any).ap_aging?.[key] || { count: 0, total_amount: 0 };
                  const hot = overdue && bucket.count > 0;
                  return (
                    <div key={key} style={{
                      background: hot ? "#fef2f2" : "#f8fafc", border: `1px solid ${hot ? "#fca5a5" : "#e2e8f0"}`,
                      borderRadius: 10, padding: "12px 14px",
                    }}>
                      <div style={{ fontSize: 12, color: hot ? "#991b1b" : "#64748b", fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: hot ? RED : "#0f172a" }}>
                        {bucket.total_amount.toLocaleString("en", { minimumFractionDigits: 2 })}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{bucket.count} bill{bucket.count !== 1 ? "s" : ""}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Spend by category + Top vendors */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="grid-2col">
            <div style={{ background: "white", borderRadius: 10, padding: "22px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Spend by Category</div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Paid bills · this month</div>
              {((apSummary as any).spend_by_category || []).length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>No paid bills this month</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={(apSummary as any).spend_by_category}
                      dataKey="total_amount"
                      nameKey="wht_category"
                      cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                      label={({ wht_category }: any) => WHT_CATEGORY_LABELS[wht_category] || wht_category}
                    >
                      {((apSummary as any).spend_by_category as any[]).map((_, i) => (
                        <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number, _n: string, p: any) => [v.toLocaleString("en", { minimumFractionDigits: 2 }), WHT_CATEGORY_LABELS[p.payload.wht_category] || p.payload.wht_category]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={{ background: "white", borderRadius: 10, padding: "22px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Top Vendors</div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>By spend · this month</div>
              {((apSummary as any).top_vendors || []).length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>No paid bills this month</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={(apSummary as any).top_vendors} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="vendor_name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={110} />
                    <Tooltip cursor={{ fill: "#f8fafc" }} formatter={(v: number) => v.toLocaleString("en", { minimumFractionDigits: 2 })} />
                    <Bar dataKey="total_spend" fill={NAVY} radius={[0, 4, 4, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
